/* Colour photo sheet - opened from a bundle slot's "<Colour> photos" button.

   A small product page for one colourway: photos you can step through, the
   other colourways as swatches, the product name and that colour's star
   rating, price and saving, pay-later amounts, delivery, sizes, and one action.

   One button (18 Sep 2026). The sheet is always opened from a bundle slot, so
   the button always writes the chosen colour and size back into that slot and
   closes - "Add <colour> · item 2" on a bundle of 2+, "Choose <colour>" when
   there is only one slot. It never adds a loose item to the cart: that left
   the bundle showing a different colour from the one just chosen, and the
   shopper outside the bundle at full price. A size must be chosen first.

   No grab handle (21 Sep 2026). The bar across the top of the sheet looked
   like a drag-to-dismiss handle and was not one, so people pulled it and
   nothing happened. Wiring a pointer drag to it did not hold up on a real
   phone either, and the sheet already closes three other ways - the X, the
   backdrop and Escape - so the bar is gone rather than fixed. Do not put it
   back without a gesture that actually works; an affordance that lies is
   worse than no affordance. The .sps-grab rule in shaped-bundle-fixes.css is
   now dead and can go whenever that file is next touched.

   Button on a phone (22 Sep 2026). .sps-add in shaped-bundle-fixes.css is a
   nowrap flex row with padding 14px 0 - no side padding at all. That held up
   while the label was short, then "Choose Brown Leopard Print instead" wrapped
   hard against both edges with the price crushed into the corner. The rules in
   injectCss() below add the side padding and let the row wrap, so on a narrow
   screen the label takes the line it needs and the price sits under it on its
   own. They live here, appended after the stylesheet, because this file owns
   the button; fold them into shaped-bundle-fixes.css next time that file is
   opened and delete injectCss(). Shorter label copy helps more than any of
   this: the sheet_use_single setting is where that lives.

   The rating reads "4.8/5" - one decimal and the scale, because a bare 4.83
   beside five stars made people count the stars to work out what it was out
   of. No review count (Sept 2026: per-colour counts like "21 Cocoa reviews"
   read small next to the store-wide 228 shown on the page).

   Thin colours (21 Sep 2026): a colour with one review was being given that
   review's score - the Pink Leopard sat at 2.00 off a single rating - and a
   colour with none showed no stars at all. Under opts.minReviews the sheet
   shows opts.rangeRating instead, the whole bundle's reviews weighted, with
   opts.rangeNote beside it. The note is not optional dressing: without it the
   number reads as this colour's own, which it is not.

   blocks/shaped-bundle-tiers.liquid owns the data and calls
   window.ShapedPhotoSheet.open(opts). This file owns only presentation.

   opts: { products, index, slot, slotLabel, unitPrice, freeAt, delivery,
           tierNote, minReviews, rangeRating, rangeCount, rangeNote,
           labels: { add, addItem, useSingle, chooseSize, added, free },
           onUse(productIndex, variantId) }

   Prices use the page format R699.95 / R1,299.00 (ShapedPayLater.money when
   shaped-pdp.js is loaded). The saving uses the theme's own sale badge classes
   so it glows like every other sale badge on the site.

   Layout is CSS-driven (assets/shaped-bundle-fixes.css, .sps-*): a two-column
   modal from 750px, a bottom sheet below it. The photo track is one
   scroll-snap strip in both, so swipe, arrows, counter and thumbnails all read
   and drive the same scroll position.
*/
(function () {
  'use strict';
  if (window.ShapedPhotoSheet) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function money(cents) {
    if (window.ShapedPayLater && typeof window.ShapedPayLater.money === 'function') return window.ShapedPayLater.money(cents);
    return 'R' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\[(\w+)\]/g, function (m, k) { return map[k] != null ? map[k] : m; });
  }

  /* Side padding and a row that is allowed to wrap. Appended after the
     stylesheet so it wins at equal specificity. See the note at the top. */
  function injectCss() {
    if (document.getElementById('sps-btn-css')) return;
    var st = el('style');
    st.id = 'sps-btn-css';
    st.textContent =
      '.sps .sps-add{padding-left:18px;padding-right:18px;flex-wrap:wrap;row-gap:1px;text-align:center;}' +
      '.sps .sps-add > span{min-width:0;}' +
      '@media (max-width:560px){' +
        '.sps .sps-add{gap:0 8px;letter-spacing:.01em;line-height:1.3;}' +
        '.sps .sps-add > span{flex:1 1 100%;}' +
        '.sps .sps-add > i{display:none;}' +
        '.sps .sps-add > em{flex:1 1 100%;font-style:normal;font-size:13px;opacity:.92;}' +
      '}';
    document.head.appendChild(st);
  }

  var root, track, counter, thumbs, swatchRow, sizeRow, msg, addBtn, useBtn, noteEl, lastFocus;
  var state = null;

  function build() {
    injectCss();
    root = el('div', 'sps');
    root.hidden = true;
    root.innerHTML =
      '<div class="sps-bg" data-sps-close></div>' +
      '<div class="sps-card" role="dialog" aria-modal="true" aria-labelledby="sps-title">' +
        '<button type="button" class="sps-x" data-sps-close aria-label="Close">&#x2715;</button>' +
        '<div class="sps-gal">' +
          '<div class="sps-stage">' +
            '<div class="sps-track" tabindex="0" aria-label="Photos"></div>' +
            '<button type="button" class="sps-arrow sps-prev" aria-label="Previous photo">&#8249;</button>' +
            '<button type="button" class="sps-arrow sps-next" aria-label="Next photo">&#8250;</button>' +
            '<span class="sps-count" aria-live="polite"></span>' +
          '</div>' +
          '<div class="sps-thumbs"></div>' +
        '</div>' +
        '<div class="sps-info">' +
          '<div class="sps-swatches" role="group" aria-label="Colour"></div>' +
          '<h2 class="sps-title" id="sps-title"></h2>' +
          '<a class="sps-rate" href="#shaped-reviews" data-scroll-reviews data-sps-close></a>' +
          '<div class="sps-price"></div>' +
          '<button type="button" class="spl-inline sps-later" data-paylater></button>' +
          '<p class="sps-ship"><span class="sps-pulse" aria-hidden="true"></span><b class="sps-ship-days"></b><span class="sps-ship-free"></span></p>' +
          '<div class="sps-sizehead"><span>Size</span><button type="button" class="sps-guide">Size guide</button></div>' +
          '<div class="sps-sizes" role="group" aria-label="Size"></div>' +
          '<p class="sps-msg" aria-live="polite"></p>' +
          '<div class="sps-actions">' +
            '<button type="button" class="sps-add"></button>' +
            '<button type="button" class="sps-use" hidden></button>' +
            '<p class="sps-note"></p>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(root);

    track = root.querySelector('.sps-track');
    counter = root.querySelector('.sps-count');
    thumbs = root.querySelector('.sps-thumbs');
    swatchRow = root.querySelector('.sps-swatches');
    sizeRow = root.querySelector('.sps-sizes');
    msg = root.querySelector('.sps-msg');
    addBtn = root.querySelector('.sps-add');
    useBtn = root.querySelector('.sps-use');
    noteEl = root.querySelector('.sps-note');

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-sps-close]')) close(false, e.target.closest('[data-scroll-reviews]'));
    });
    document.addEventListener('keydown', function (e) {
      if (root.hidden) return;
      if (e.key === 'Escape' && !document.querySelector('.spl-sheet:not([hidden])')) close();
      if (e.key === 'ArrowRight' && e.target === track) step(1);
      if (e.key === 'ArrowLeft' && e.target === track) step(-1);
    });
    root.querySelector('.sps-prev').addEventListener('click', function () { step(-1); });
    root.querySelector('.sps-next').addEventListener('click', function () { step(1); });
    root.querySelector('.sps-guide').addEventListener('click', function () {
      var drawer = document.querySelector('[id^="size-guide-drawer-"]');
      if (!drawer) return;
      close(true, true);
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });

    var raf = 0;
    track.addEventListener('scroll', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncCount);
    }, { passive: true });

    addBtn.addEventListener('click', function () {
      if (isSlotFill()) { useInBundle(); return; }
      addToBag();
    });
  }

  function slides() { return track.querySelectorAll('.sps-slide'); }
  function currentIndex() {
    var s = slides();
    if (!s.length) return 0;
    var left = track.scrollLeft, best = 0, dist = Infinity;
    for (var i = 0; i < s.length; i++) {
      var d = Math.abs(s[i].offsetLeft - track.offsetLeft - left);
      if (d < dist) { dist = d; best = i; }
    }
    return best;
  }
  function goTo(i) {
    var s = slides();
    if (!s.length) return;
    i = Math.max(0, Math.min(s.length - 1, i));
    track.scrollTo({ left: s[i].offsetLeft - track.offsetLeft, behavior: REDUCED ? 'auto' : 'smooth' });
  }
  function step(d) { goTo(currentIndex() + d); }
  function syncCount() {
    var s = slides(), i = currentIndex();
    counter.textContent = s.length ? (i + 1) + ' / ' + s.length : '';
    root.querySelector('.sps-prev').disabled = i <= 0;
    root.querySelector('.sps-next').disabled = i >= s.length - 1;
    [].forEach.call(thumbs.children, function (t, j) { t.setAttribute('aria-current', j === i ? 'true' : 'false'); });
  }

  function stars(rating) {
    var full = Math.round(rating || 0), out = '';
    for (var i = 1; i <= 5; i++) out += i <= full ? '★' : '☆';
    return out;
  }
  /* One decimal and the scale: "4.8/5", not "4.83". */
  function score(rating) {
    return (Math.round((rating || 0) * 10) / 10).toFixed(1);
  }

  /* Whose rating is this? A colour with enough reviews speaks for itself.
     Below that the bundle speaks for it, and says so. */
  function ratingFor(p, o) {
    var floor = (o.minReviews == null) ? 5 : o.minReviews;
    var count = parseInt(p.reviews, 10) || 0;
    var own = parseFloat(p.rating) || 0;
    if (count >= floor && own > 0) return { value: own, note: '' };
    var range = parseFloat(o.rangeRating) || 0;
    if (range > 0) return { value: range, note: o.rangeNote || 'For Our Range' };
    return null;
  }

  function render() {
    var o = state.opts, p = o.products[state.index], L = o.labels || {};
    var name = p.name;

    // photos
    track.textContent = '';
    thumbs.textContent = '';
    var photos = (p.media && p.media.length) ? p.media : [p.img];
    photos.forEach(function (src, i) {
      var slide = el('div', 'sps-slide');
      var img = el('img');
      img.src = src;
      img.alt = name + ' — photo ' + (i + 1) + ' of ' + photos.length;
      img.loading = i < 2 ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);

      var t = el('button', 'sps-thumb');
      t.type = 'button';
      t.setAttribute('aria-label', 'Show photo ' + (i + 1));
      var ti = el('img');
      ti.src = src;
      ti.alt = '';
      ti.loading = 'lazy';
      t.appendChild(ti);
      t.addEventListener('click', function () { goTo(i); });
      thumbs.appendChild(t);
    });
    track.scrollLeft = 0;
    syncCount();

    // colour swatches
    swatchRow.textContent = '';
    o.products.forEach(function (op, i) {
      var b = el('button', 'sps-sw');
      b.type = 'button';
      b.setAttribute('aria-pressed', i === state.index ? 'true' : 'false');
      b.setAttribute('aria-label', op.name);
      var im = el('img');
      im.src = op.img;
      im.alt = '';
      b.appendChild(im);
      b.appendChild(el('span', null, op.name));
      b.addEventListener('click', function () {
        if (i === state.index) return;
        state.index = i;
        state.variant = null;
        render();
      });
      swatchRow.appendChild(b);
    });

    root.querySelector('.sps-title').textContent = p.title || name;

    var rate = root.querySelector('.sps-rate');
    var r = ratingFor(p, o);
    if (r) {
      var shown = score(r.value);
      rate.hidden = false;
      rate.textContent = '';
      rate.setAttribute('aria-label', 'Rated ' + shown + ' out of 5' + (r.note ? ' ' + r.note : '') + '. See reviews.');
      rate.appendChild(el('span', 'sps-stars', stars(r.value)));
      rate.appendChild(el('b', null, shown + '/5'));
      if (r.note) {
        var noteSpan = el('span', 'sps-rate-note', r.note);
        noteSpan.style.cssText = 'margin-left:6px;font-weight:400;opacity:.72;';
        rate.appendChild(noteSpan);
      }
    } else {
      rate.hidden = true;
    }

    /* While a bundle slot is waiting, the price shown is the one this colour
       will actually cost in that bundle - anything else invites the shopper to
       do arithmetic the panel has already done. */
    var slotFill = isSlotFill();
    var showUnit = (slotFill && o.unitPrice && o.unitPrice < p.price) ? o.unitPrice : null;
    var price = root.querySelector('.sps-price');
    price.textContent = '';
    price.appendChild(el('span', 'sps-now', money(showUnit || p.price)));
    if (showUnit) {
      price.appendChild(el('s', 'sps-was', money(p.price)));
      price.appendChild(el('span', 'p_badge on_sale animmate_product_badge sps-badge', L.bundlePrice || 'Bundle price'));
    } else if (p.compare > p.price) {
      price.appendChild(el('s', 'sps-was', money(p.compare)));
      price.appendChild(el('span', 'p_badge on_sale animmate_product_badge sps-badge', 'Save ' + money(p.compare - p.price)));
    }

    var later = root.querySelector('.sps-later');
    if (window.ShapedPayLater) {
      later.hidden = false;
      window.ShapedPayLater.fillInline(later, showUnit || p.price);
    } else {
      later.hidden = true;
    }

    root.querySelector('.sps-ship-days').textContent = o.delivery || '';
    root.querySelector('.sps-ship-free').textContent = o.freeAt ? ' · ' + fill(L.free || 'free over [amount]', { amount: money(o.freeAt).replace(/\.00$/, '') }) : '';

    // sizes
    sizeRow.textContent = '';
    p.variants.forEach(function (v) {
      var b = el('button', 'sps-size', v.title);
      b.type = 'button';
      b.disabled = !v.available;
      if (!v.available) b.setAttribute('aria-label', v.title + ', sold out');
      b.setAttribute('aria-pressed', state.variant === v.id ? 'true' : 'false');
      b.addEventListener('click', function () {
        state.variant = v.id;
        msg.textContent = '';
        [].forEach.call(sizeRow.children, function (c) { c.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
      });
      sizeRow.appendChild(b);
    });
    msg.textContent = '';

    /* The button fills the slot that opened the sheet. It names the slot when
       the bundle has more than one. */
    var toSlot = slotFill;
    addBtn.disabled = false;
    addBtn.textContent = '';
    addBtn.appendChild(el('span', null, toSlot
      ? (o.slotLabel
          ? fill(L.addItem || 'Add [colour] · [item]', { colour: name, item: o.slotLabel })
          : fill(L.useSingle || 'Choose [colour]', { colour: name }))
      : fill(L.add || 'Add [colour] to bag', { colour: name })));
    addBtn.appendChild(el('i', null));
    addBtn.appendChild(el('em', null, money(toSlot && o.unitPrice ? o.unitPrice : p.price)));
    /* The second button is gone: one sheet, one action. */
    useBtn.hidden = true;
    noteEl.textContent = o.tierNote || '';
    noteEl.hidden = !o.tierNote;
  }

  /* Kept for any caller that opens the sheet without a bundle slot behind it.
     Nothing on the product page does today. */
  function addToBag() {
    if (!state.variant) { wantSize(); return; }
    addBtn.disabled = true;
    var label = addBtn.querySelector('span');
    var was = label.textContent;
    label.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: state.variant, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      label.textContent = (state.opts.labels && state.opts.labels.added) || 'Added to bag';
      var dc = document.querySelector('drawer-cart');
      setTimeout(function () {
        close(true, true);
        if (dc && typeof dc.fetchAndRenderCartItems === 'function') {
          document.body.style.overflow = '';
          dc.fetchAndRenderCartItems();
        } else {
          window.location.href = '/cart';
        }
      }, 500);
    }).catch(function () {
      addBtn.disabled = false;
      label.textContent = was;
      msg.textContent = 'That didn’t go through. Please try again.';
    });
  }

  /* The sheet is always opened from a bundle slot, so the button always writes
     the colour and size back into that slot. It never adds a loose item to the
     cart: doing so left the bundle showing a different colour from the one just
     chosen, which is what the shopper thought they were choosing. */
  function isSlotFill() {
    var o = state && state.opts;
    return !!(o && typeof o.onUse === 'function');
  }

  function useInBundle() {
    var o = state.opts;
    if (!state.variant) { wantSize(); return; }
    o.onUse(state.index, state.variant);
    close();
  }

  /* Same prompt for both actions: say it, shake the sizes, put the cursor on
     the first one that is in stock. */
  function wantSize() {
    msg.textContent = (state.opts.labels && state.opts.labels.chooseSize) || 'Choose a size';
    sizeRow.classList.remove('is-shake');
    void sizeRow.offsetWidth;
    sizeRow.classList.add('is-shake');
    var first = sizeRow.querySelector('.sps-size:not([disabled])');
    if (first) first.focus({ preventScroll: true });
  }

  function open(opts) {
    if (!root) build();
    lastFocus = document.activeElement;
    state = { opts: opts, index: opts.index || 0, variant: null };
    render();
    root.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      syncCount();
      var x = root.querySelector('.sps-x');
      if (x) x.focus({ preventScroll: true });
    });
  }

  // keepLock: another overlay is taking over, leave the page scroll locked.
  // skipFocus: the page is about to move elsewhere (reviews, size guide, cart),
  // so don't pull focus - and the scroll position - back to the opener.
  function close(keepLock, skipFocus) {
    if (!root || root.hidden) return;
    root.hidden = true;
    if (!keepLock) document.body.style.overflow = '';
    if (!skipFocus && lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  window.ShapedPhotoSheet = { open: open, close: close };
})();